import { useRef, type ReactNode } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useActiveAppStore } from "../../../store/activeAppStore";
import { useUserStore } from "../../../store/userStore";
import { useLiteNotifications } from "../../../features/notifications/liteQueries";
import { useMenusCabecera } from "../../../store/menusCabecera";
import BotonDeCabecera from "../menus/BotonDeCabecera";
import PanelAnclado from "../menus/PanelAnclado";
import MenuDeAvisos from "../menus/MenuDeAvisos";
import type { Company } from "../../../store/admin/companiesStore";
import { CentinelaChat } from "../CentinelaChat";
import UserInitials from "../UserInitials";
import { CompanyMenu } from "../CompanyMenu";
import { LogoImage } from "../LogoImage";
import { AppTitle } from "../AppTitle";
import { useIsReseller } from "../../../hooks/useIsReseller";
import { useTheme } from "../../../context/ThemeContext";
import { getLogoMain } from "../../../config/brandAssets";
import { Button } from "../../ui";


export interface SearchBarProps {
  searchQuery?: string;
  onSearchChange?: (query: string) => void;
  onSearchClear?: () => void;
  searchPlaceholder?: string;
  searchResultCount?: number;
  searchTotalCount?: number;
}

export interface HeaderProps {
  title?: string;
  subtitle?: string;
  onOpenAppsSidebar?: () => void;
  onToggleMobileMenu?: () => void;
  isMobileMenuOpen?: boolean;
  isMobile?: boolean;
  isReseller?: boolean;
  companies?: Company[];
  selectedCompany?: string | null;
  onCompanyChange?: (company: string | null) => void;
  customActions?: ReactNode;
  showLogo?: boolean;
  showSearchBar?: boolean;
  searchBarProps?: SearchBarProps;
  hideProfileButton?: boolean;
  /**
   * Un globo de ayuda anclado al asistente de Centinela.
   *
   * Lo pone quien lo necesita —hoy el mapa de unidades, para presentar la
   * vista táctica— en vez de que el header conozca funciones de una app
   * concreta. Llega como nodo y el header sólo le da su sitio.
   */
  aiCoach?: ReactNode;
  hideCompanySelector?: boolean;
  showCompanySelector?: boolean;
  isAppsSidebarOpen?: boolean;
  showWordmark?: boolean;
  wordmarkSrc?: string;
  wordmarkClassName?: string;
  hideThemeToggle?: boolean;
  hideAiAssistant?: boolean;
}

export default function Header({
  title,
  subtitle,
  onOpenAppsSidebar,
  onToggleMobileMenu,
  isMobileMenuOpen,
  isMobile = false,
  isReseller = false,
  companies = [],
  selectedCompany,
  onCompanyChange,
  customActions,
  showLogo = true,
  showSearchBar = false,
  searchBarProps,
  hideProfileButton = false,
  aiCoach,
  hideCompanySelector = false,
  showCompanySelector = false,
  isAppsSidebarOpen = false,
  showWordmark = false,
  wordmarkSrc,
  wordmarkClassName = "h-9 w-auto flex-shrink-0",
  hideThemeToggle = false,
  hideAiAssistant = false,
}: HeaderProps) {
  const { t } = useTranslation("common");
  const navigate = useNavigate();
  const { isReseller: isResellerRole } = useIsReseller();
  const { isDarkMode, toggleTheme } = useTheme();

  const user = useUserStore((state) => state.user);
  const { unreadCount } = useLiteNotifications();

  /*
    Los tres botones viven aquí, pero dos de los tres paneles los pinta
    `AppsSidebar`, que es quien recibe de cada layout el catálogo y las
    acciones de cuenta. Por eso cuál está abierto es un dato compartido y no
    estado local: moverlo por props obligaría a tocar los 44 layouts.
  */
  const menuAbierto = useMenusCabecera((s) => s.abierto);
  const alternarMenu = useMenusCabecera((s) => s.alternar);
  const cerrarMenu = useMenusCabecera((s) => s.cerrar);
  const refAvisos = useRef<HTMLButtonElement>(null);
  const refApps = useRef<HTMLButtonElement>(null);
  const refCuenta = useRef<HTMLButtonElement>(null);

  const activeAppTitle = useActiveAppStore((state) => state.title);
  const activeAppSubtitle = useActiveAppStore((state) => state.subtitle);
  const activeAppIcon = useActiveAppStore((state) => state.icon);
  const activeAppIconColor = useActiveAppStore((state) => state.iconColor);

  const displayTitle = title || activeAppTitle || t("header.appTitle");
  const displaySubtitle =
    subtitle || activeAppSubtitle || t("header.dashboard");


  const themeClasses = {
    bg: "bg-surface",
    border: "border-line",
    textSecondary: "text-content-muted",
    cardBg: "bg-card",
    cardBorder: "border-line",
    buttonHover: "hover:bg-subtle",
    buttonText: "text-content-muted",
    buttonTextHover: "hover:text-content",
    inputBg: "bg-app",
    inputBorder: "border-line",
    inputText: "text-content",
    inputPlaceholder: "placeholder-content-faint",
  };

  const showCompanyPicker =
    (isReseller || showCompanySelector) && !isMobile && !hideCompanySelector;

  return (
    <header
      className={`${themeClasses.bg} border-b ${themeClasses.border} ${isMobile ? "px-3" : "px-4 md:px-6"}`}
    >
      <div className="flex items-center justify-between gap-2 h-16">
        <div className="flex items-center gap-2 md:gap-3 min-w-0">
          {onToggleMobileMenu && (
            <Button
              variant="ghost"
              size="sm"
              iconOnly
              onClick={onToggleMobileMenu}
              className="lg:hidden"
              aria-label="Toggle menu"
              icon={
                <i
                  className={`${isMobileMenuOpen ? "ri-close-line" : "ri-menu-line"} text-lg`}
                />
              }
            />
          )}
          <button
            type="button"
            onClick={() => navigate(isResellerRole ? "/apps" : "/map")}
            className="flex items-center gap-2 min-w-0 rounded-lg transition-opacity hover:opacity-80"
            aria-label={t("header.goToApps")}
          >
            {showWordmark ? (
              <img
                src={wordmarkSrc ?? getLogoMain(isDarkMode)}
                alt={displayTitle}
                className={wordmarkClassName}
              />
            ) : (
              <>
                {showLogo && (
                  <LogoImage
                    size="sm"
                    appIcon={activeAppIcon}
                    appIconColor={activeAppIconColor}
                  />
                )}
                <AppTitle
                  title={displayTitle}
                  subtitle={displaySubtitle}
                  hideOnMobile={false}
                />
              </>
            )}
          </button>

          {showCompanyPicker && (
            <>
              <div className="hidden md:block h-6 w-px bg-line flex-shrink-0" />
              <CompanyMenu
                value={selectedCompany ?? null}
                onChange={(id) => onCompanyChange?.(id)}
                isClearable
                companies={companies.length > 0 ? companies : undefined}
                className="hidden md:block"
              />
            </>
          )}
        </div>

        {showSearchBar && !isMobile && searchBarProps && (
          <div className="flex-1 max-w-md mx-4" data-tour="header-search">
            <div className="relative">
              <i
                className={`ri-search-line absolute left-3 top-1/2 transform -translate-y-1/2 ${themeClasses.buttonText} text-sm`}
              ></i>
              <input
                type="text"
                value={searchBarProps.searchQuery || ""}
                onChange={(e) =>
                  searchBarProps.onSearchChange?.(e.target.value)
                }
                placeholder={
                  searchBarProps.searchPlaceholder ||
                  t("header.searchPlaceholder")
                }
                className={`w-full pl-10 pr-16 py-1.5 ${themeClasses.inputBg} border ${themeClasses.inputBorder} rounded-xl ${themeClasses.inputText} ${themeClasses.inputPlaceholder} focus:outline-none focus:ring-2 focus:ring-primary-500/40 focus:border-transparent transition-all text-sm`}
              />
              {searchBarProps.searchQuery && (
                <button
                  onClick={searchBarProps.onSearchClear}
                  className={`absolute right-12 top-1/2 transform -translate-y-1/2 ${themeClasses.buttonText} hover:text-error`}
                >
                  <i className="ri-close-line text-sm"></i>
                </button>
              )}
              {searchBarProps.searchResultCount !== undefined && (
                <span
                  className={`absolute right-3 top-1/2 transform -translate-y-1/2 text-xs ${themeClasses.textSecondary}`}
                >
                  {searchBarProps.searchResultCount}/
                  {searchBarProps.searchTotalCount}
                </span>
              )}
            </div>
          </div>
        )}

        <div className="flex items-center gap-1 flex-shrink-0">
          {!hideThemeToggle && (
            <Button
              variant="ghost"
              size="sm"
              iconOnly
              onClick={toggleTheme}
              title={t(isDarkMode ? "theme.light" : "theme.dark")}
              aria-label={t(isDarkMode ? "theme.light" : "theme.dark")}
              icon={
                <i
                  className={`${isDarkMode ? "ri-sun-line" : "ri-moon-line"} text-base`}
                />
              }
            />
          )}



          {customActions}

          {!hideAiAssistant && (
            // `relative` para que el globo se ancle AQUÍ, bajo el avatar, y no
            // a la ventana: así su punta apunta al control del que habla.
            <div className="relative">
              <CentinelaChat />
              {aiCoach}
            </div>
          )}

          {/*
            Tres controles, uno por cosa.

            Antes era UN botón con tres significados encima: las iniciales del
            usuario (identidad), un punto rojo de avisos (estado) y un galón de
            cuadrícula (lanzador). Pulsaras lo que pulsaras, se abría el mismo
            cajón, y el punto rojo no llevaba número: un aviso y cuarenta se
            veían igual.
          */}
          {!hideProfileButton && (
            <div className="flex items-center gap-0.5">
              <BotonDeCabecera
                ref={refAvisos}
                icono="ri-notification-3-line"
                etiqueta={t("notifications.title", "Avisos")}
                activo={menuAbierto === "avisos"}
                onClick={() => alternarMenu("avisos")}
                cuenta={unreadCount}
              />
              <BotonDeCabecera
                ref={refApps}
                icono="ri-apps-2-line"
                ancla="apps"
                etiqueta={t("header.openAppsPanel")}
                activo={menuAbierto === "apps" || isAppsSidebarOpen}
                onClick={() => {
                  alternarMenu("apps");
                  onOpenAppsSidebar?.();
                }}
              />
              <button
                ref={refCuenta}
                type="button"
                data-ancla-menu="cuenta"
                onClick={() => alternarMenu("cuenta")}
                aria-haspopup="dialog"
                aria-expanded={menuAbierto === "cuenta"}
                aria-label={t("appsSidebar.account", "Cuenta")}
                className={`ml-0.5 flex h-11 w-11 items-center justify-center rounded-full transition-colors md:h-9 md:w-9 ${
                  menuAbierto === "cuenta" ? "ring-2 ring-primary-500" : "hover:opacity-85"
                }`}
              >
                <span className="flex h-8 w-8 items-center justify-center rounded-full bg-primary-500 text-xs font-bold text-on-primary ring-2 ring-line/60">
                  <UserInitials user={user} iconClassName="text-base" />
                </span>
              </button>

              {menuAbierto === "avisos" && (
                <PanelAnclado
                  abierto
                  onCerrar={cerrarMenu}
                  anclaRef={refAvisos}
                  etiqueta={t("notifications.title", "Avisos")}
                  ancho={340}
                >
                  <MenuDeAvisos onCerrar={cerrarMenu} />
                </PanelAnclado>
              )}
            </div>
          )}
        </div>
      </div>
    </header>
  );
}